import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { NgbDateParserFormatter, NgbTabset } from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import { forkJoin, Subscription } from 'rxjs';
import { concatMap, finalize, map, take, tap } from 'rxjs/operators';
import { utils, WorkBook, WorkSheet, writeFile } from 'xlsx';

import { Util } from '../../common/common.util';
import { TranslationKeyMap } from '../reports.i18n';
import { ReportsService } from '../reports.service';
import { TdRawAgentDataDates } from '../td-daily-huddle/td-daily-huddle.reports.model';

import {
    RxrData,
    RxRMetric,
    RxRMetrics,
    RxRReportSelectionParams,
} from './rxr.reports.component.model';
import { RxRService } from './rxr.reports.service';

enum Tabs {
    PARAMS = 'w-tab-report-0',
    GRID = 'w-tab-report-1',
}

@Component({
    selector: '[rxrReport]',
    templateUrl: './rxr.reports.component.html',
    host: {
        class: 'cmps-reports-rxr',
    },
})
export class RxRReportComponent implements AfterViewInit, OnDestroy, OnInit {
    @ViewChild('tabSet', { static: true }) tabSet: NgbTabset;
    @ViewChild('table') table: ElementRef<HTMLTableElement>;
    i18n = TranslationKeyMap;
    pendingData: boolean;
    rawDates: TdRawAgentDataDates;
    reportData: RxrData[] = new Array<RxrData>();
    backReportData: RxrData[] = new Array<RxrData>();
    selectedMetrics: RxRMetrics[] = new Array<RxRMetrics>();
    roles: IOption[];

    usernameFilter: boolean;
    roleFilter: boolean;
    supergroupFilter: boolean;

    selectedUser: RxrData;
    selectedRole: RxrData;
    selectedDate: string;
    selectedDateRange: string;
    startDate: string;
    endDate: string;
    generatedDate: string;
    reportId: number;

    params: RxRReportSelectionParams = {
        date: null,
        dateRange: null,
        metrics: [],
        role: null,
        user: null,
        invalidUser: true,
        noData: true,
        invalidDate: false,
        noFound: false,
        pendingData: false,
    };

    availableMetrics: RxRMetrics[] = [];
    sub: Subscription;

    constructor(
        private _rxrService: RxRService,
        private readonly _reportService: ReportsService,
    ) {}

    ngOnInit(): void {
        this.params.pendingData = false;
        this.params.noData = true;
        this.params.noFound = false;

        forkJoin([
            this._reportService.retrieveConfigReportSelectedJson(3).pipe(
                take(1),
                map((config) => {
                    const params = config.kpiSelectionControls[0].parameters;
                    const metricsOutcomes = params.find(
                        (p) => p.name === 'metricsOutcomes',
                    ).value as number[];
                    const metricsBehaviours = params.find(
                        (p) => p.name === 'metricsBehaviours',
                    ).value as number[];
                    return metricsOutcomes.concat(metricsBehaviours);
                }),
                concatMap((metricIds) =>
                    this._reportService.retrieveMetricNames(
                        metricIds.join(','),
                    ),
                ),
                tap((metrics) => {
                    this.availableMetrics = (metrics as RxRMetrics[]).map(
                        (m) => ({
                            ...m,
                            data: { isEditing: false, value: null },
                        }),
                    );
                    console.log('availableMetrics', [...this.availableMetrics]);
                }),
            ),
            this._reportService.retrieveRawAgentDataDates().pipe(
                take(1),
                tap((rawDates) => {
                    this.rawDates = rawDates;
                    this.params.date = Util.dateToNgbDate(
                        new Date(this.rawDates.lastDate),
                    );
                }),
            ),
            this._reportService.retrieveDefaultHierarchyUser().pipe(
                take(1),
                tap((user) => {
                    this.params.user = user;
                }),
            ),
        ])
            .pipe(
                tap(() => {
                    this.onChanged();
                }),
                finalize(() => {
                    this.params.pendingData = false;
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onChanged() {
        this.params.noFound = false;
        if (this.params.user && this.params.date) {
            this.params.pendingData = true;

            this._reportService
                .retrieveRolesHierarchy(
                    this.params.user.userId,
                    Util.dateToDate(this.params.date),
                )
                .pipe(
                    take(1),
                    map((roles) =>
                        roles.map((r) => ({
                            value: r.id.toString(),
                            label: r.name,
                        })),
                    ),
                    tap((roles) => {
                        if (roles.length > 0) {
                            this.roles = roles;
                            this.params.role = roles[0].value;
                        }
                    }),
                    // eslint-disable-next-line no-return-assign
                    finalize(() => (this.params.pendingData = false)),
                )
                .subscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onGeneratedReport(reportId: number) {
        this.reportId = reportId;
        this.getReportResult();
        this.tabSet.select('w-tab-report-1');
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngAfterViewInit() {
        this.sub = this.tabSet.tabChange
            .pipe(
                tap((t) => {
                    if (t.nextId === Tabs.GRID) {
                        this.getReportResult();
                    }
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getReportResult() {
        if (this.reportId) {
            this.pendingData = true;
            this._reportService
                .retrieveGenericReportResult(this.reportId)
                .pipe(
                    take(1),
                    tap((result) => {
                        this._rxrService
                            .generateParameters(result)
                            .pipe(
                                tap((s) => {
                                    this.selectedRole =
                                        this._rxrService.selectedRole;
                                    this.selectedUser =
                                        this._rxrService.selectedUser;
                                    this.selectedDate =
                                        this._rxrService.selectedDate;
                                    this.generatedDate =
                                        this._rxrService.generatedDate;
                                    this.startDate =
                                        this._rxrService.selectedPeriod.StartDate;
                                    this.endDate =
                                        this._rxrService.selectedPeriod.EndDate;
                                    this._rxrService.selectedDateRange =
                                        this.params.dateRange;
                                    if (this._rxrService.backReportData) {
                                        this.selectedMetrics =
                                            this._rxrService.backReportData
                                                .reduce<RxRMetric[]>((o, c) => {
                                                    o.push(
                                                        ...c.metrics.filter(
                                                            (cm) =>
                                                                !o.some(
                                                                    (m) =>
                                                                        Number(
                                                                            m.metricID,
                                                                        ) ===
                                                                        Number(
                                                                            cm.metricID,
                                                                        ),
                                                                ),
                                                        ),
                                                    );
                                                    return o;
                                                }, [])
                                                .map((m) => {
                                                    return this.availableMetrics.find(
                                                        (am) =>
                                                            m.metricID ===
                                                            am.id.toString(),
                                                    );
                                                });

                                        this.selectedMetrics =
                                            this.selectedMetrics.filter((m) => {
                                                return m !== undefined;
                                            });
                                        this._rxrService.selectedMetrics =
                                            this.selectedMetrics;
                                        this.applyFilters();
                                        this.params.noData = false;
                                        this.params.noFound = false;
                                    } else {
                                        this.params.noData = true;
                                        this.params.noFound = true;
                                    }
                                    this.usernameFilter = false;
                                    this.roleFilter = false;
                                    this.supergroupFilter = false;
                                }),
                            )
                            .subscribe();
                    }),
                    finalize(() => {
                        this.pendingData = false;
                    }),
                )
                .subscribe();
        } else {
            this.params.noData = true;
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onExportClick() {
        const workSheet: WorkSheet = utils.table_to_sheet(
            this.table.nativeElement,
        );
        const workBook: WorkBook = utils.book_new();
        utils.book_append_sheet(workBook, workSheet, 'Page 1');
        writeFile(workBook, 'Report RxR.xlsx', {
            bookType: 'xlsx',
            type: 'buffer',
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toogleUserNameFilter() {
        this.usernameFilter = !this.usernameFilter;
        if (!this.usernameFilter) {
            this._rxrService.userNameFilterQuery = '';
            this.applyFilters();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toogleRoleFilter() {
        this.roleFilter = !this.roleFilter;
        if (!this.roleFilter) {
            this._rxrService.roleFilterQuery = '';
            this.applyFilters();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toogleSuperGroupFilter() {
        this.supergroupFilter = !this.supergroupFilter;
        if (!this.supergroupFilter) {
            this._rxrService.superGroupFilterQuery = '';
            this.applyFilters();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    updateUserNameQuery(value) {
        this._rxrService.userNameFilterQuery = value;
        this.applyFilters();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    updateRoleQuery(value) {
        this._rxrService.roleFilterQuery = value;
        this.applyFilters();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    updateSuperGroupQuery(value) {
        this._rxrService.superGroupFilterQuery = value;
        this.applyFilters();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    applyFilters() {
        this._rxrService.filterTable().subscribe((model) => {
            this.reportData = model;
        });
    }
}
